import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { captureException, fetchAllData } from 'r/utils';
import { GET_EVENTS_PLAY } from 'r/utils/fetchApiRaw';
import type { EventOutcome, SanseiEvent } from 'r/types';

// A KOL is "new" when the first event for this author was created recently
export const NEW_KOL_PERIOD_DAYS = 14;

export type EventResult = 'hit' | 'miss' | 'neutral' | 'pending';

export interface EventKol {
  kolID: string;
  username: string;
  name: string;
  avatar: string | null;
  events: SanseiEvent[];
  eventsCount: number;
  activeCount: number;
  resolvedCount: number;
  hits: number;
  misses: number;
  neutrals: number;
  // hits / (hits + misses), null when there is nothing to compare yet
  hitRate: number | null;
  firstEventAt: string;
  lastEventAt: string;
  isNew: boolean;
}

export type KolSortKey =
  | 'firstEventAt_desc'
  | 'lastEventAt_desc'
  | 'eventsCount_desc'
  | 'hitRate_desc'
  | 'username_asc';

export const kolListFiltersAtom = atom({
  search: '',
  onlyNew: false,
  sortBy: 'firstEventAt_desc' as KolSortKey,
  page: 0,
  limit: 50,
});

const getOutcomeDirection = (
  outcome: EventOutcome,
): 'up' | 'down' | 'range' => {
  if (outcome.moreThanValue && !outcome.lessThanValue) return 'up';
  if (outcome.lessThanValue && !outcome.moreThanValue) return 'down';
  return 'range';
};

/**
 * Compares the resolved outcome with the KOL's predicted direction.
 * Range outcomes (e.g. "100-110") carry no direction, so they are neutral.
 */
export const getEventResult = (event: SanseiEvent): EventResult => {
  if (event.status !== 'RESOLVED') return 'pending';
  const trueIds =
    event.trueOutcomeIds ?? (event.trueOutcomeId ? [event.trueOutcomeId] : []);
  const trueOutcome = event.eventOutcomes?.find((o) => trueIds.includes(o.id));
  if (!trueOutcome) return 'pending';

  const direction = getOutcomeDirection(trueOutcome);
  if (direction === 'range') return 'neutral';
  return (direction === 'up') === event.isDirectionUp ? 'hit' : 'miss';
};

const groupEventsByKol = (events: SanseiEvent[]): EventKol[] => {
  const groups = new Map<string, SanseiEvent[]>();
  for (const event of events) {
    const list = groups.get(event.kolID);
    if (list) list.push(event);
    else groups.set(event.kolID, [event]);
  }

  const newSince = Date.now() - NEW_KOL_PERIOD_DAYS * 24 * 60 * 60 * 1000;

  return [...groups.entries()].map(([kolID, kolEvents]) => {
    const sorted = [...kolEvents].sort((a, b) =>
      (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
    const latest = sorted[0]!;
    const firstEventAt = sorted[sorted.length - 1]!.createdAt ?? '';

    let hits = 0;
    let misses = 0;
    let neutrals = 0;
    for (const event of sorted) {
      const result = getEventResult(event);
      if (result === 'hit') hits++;
      else if (result === 'miss') misses++;
      else if (result === 'neutral') neutrals++;
    }

    return {
      kolID,
      username: latest.kolUsername,
      name: latest.kolName,
      avatar: latest.kolAvatar || null,
      events: sorted,
      eventsCount: sorted.length,
      activeCount: sorted.filter((e) => e.status !== 'RESOLVED').length,
      resolvedCount: sorted.filter((e) => e.status === 'RESOLVED').length,
      hits,
      misses,
      neutrals,
      hitRate: hits + misses > 0 ? hits / (hits + misses) : null,
      firstEventAt,
      lastEventAt: latest.createdAt ?? '',
      isNew: new Date(firstEventAt).getTime() >= newSince,
    };
  });
};

// All non-deleted play events. Key shares the 'cms-events-play' prefix so
// existing event mutations invalidate it too.
export const allEventsQueryAtom = atomWithQuery(() => ({
  queryKey: ['cms-events-play', 'all'],
  queryFn: async () => {
    try {
      const { data, total } = await fetchAllData<SanseiEvent>(GET_EVENTS_PLAY, {
        // Play API rejects the Sanitizer token; events are public
        noAuth: true,
        queryParams: { include: 'eventOutcomes' },
      });
      return { data, total };
    } catch (e) {
      captureException(e);
      throw e;
    }
  },
  staleTime: 5 * 60 * 1000,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
}));

export const eventKolsAtom = atom((get) => {
  const events = get(allEventsQueryAtom).data?.data;
  return events ? groupEventsByKol(events) : null;
});

export const filteredEventKolsAtom = atom((get) => {
  const kols = get(eventKolsAtom);
  if (!kols) return null;
  const filters = get(kolListFiltersAtom);

  const search = filters.search.trim().toLowerCase();
  const list = kols.filter(
    (kol) =>
      (!filters.onlyNew || kol.isNew) &&
      (!search ||
        kol.username?.toLowerCase().includes(search) ||
        kol.name?.toLowerCase().includes(search)),
  );

  const compare: Record<KolSortKey, (a: EventKol, b: EventKol) => number> = {
    firstEventAt_desc: (a, b) => b.firstEventAt.localeCompare(a.firstEventAt),
    lastEventAt_desc: (a, b) => b.lastEventAt.localeCompare(a.lastEventAt),
    eventsCount_desc: (a, b) => b.eventsCount - a.eventsCount,
    hitRate_desc: (a, b) => (b.hitRate ?? -1) - (a.hitRate ?? -1),
    username_asc: (a, b) => (a.username ?? '').localeCompare(b.username ?? ''),
  };

  return list.sort(compare[filters.sortBy]);
});
